import React, { useEffect, useMemo } from 'react'
import noop from 'lodash/noop'

import { useDevice } from 'src/hooks/useDevice'
import { DEVICE_PHOTOS_INFO, getResponsiveImgProps } from 'src/comps/Gallery/utils'
import { ImageApiObject } from 'src/types/image'

import { Container, PhotosOverviewBase, StyledPhoto } from './styles'
import { IMAGE_LOADING, IMAGE_LOADING_EAGER, IMAGE_LOADING_LAZY } from '../Image/constants'

const sizes = {
  desktop: {
    size: '580px',
    srcWidth: 580,
  },
  tablet: {
    size: '943px',
    srcWidth: 943,
  },
  mobile: {
    size: '100vw',
    srcWidth: 727,
  },
}

interface Props {
  focusOnImageIndex?: number
  onPhotoClick?: (index: number) => void
  photos: ImageApiObject[]
}

const PhotosOverview = ({ focusOnImageIndex = 0, onPhotoClick = noop, photos }: Props) => {
  const { device } = useDevice()

  const photosWithLoadingType = useMemo(
    () =>
      photos.map((photo, index) => {
        const loading: (typeof IMAGE_LOADING)[number] =
          (index !== focusOnImageIndex + DEVICE_PHOTOS_INFO[device].indexOfPhotoToLazy &&
            index < focusOnImageIndex) ||
          index > focusOnImageIndex + DEVICE_PHOTOS_INFO[device].indexOfPhotoToLazy
            ? IMAGE_LOADING_LAZY
            : IMAGE_LOADING_EAGER

        return {
          ...getResponsiveImgProps(photo, sizes),
          alt: `photo-${index}`,
          loading,
        }
      }),
    [photos, device, focusOnImageIndex]
  )

  useEffect(() => {
    if (focusOnImageIndex === 0) return

    const element = document.getElementById(`photo-${focusOnImageIndex}`)
    element?.scrollIntoView()
  }, [focusOnImageIndex])

  return (
    <PhotosOverviewBase>
      <Container data-testid="photos-overview">
        {photosWithLoadingType.map((photo, index) => (
          <StyledPhoto
            alt={`photo-${index}`}
            aspectRatio="3/2"
            id={`photo-${index}`}
            key={`photo-${index + 1}`}
            onPhotoClick={() => onPhotoClick(index)}
            photo={photo}
          />
        ))}
      </Container>
    </PhotosOverviewBase>
  )
}

export default PhotosOverview
